/**
 * Pick the disk-backup target for the current platform, by capability.
 *
 * WHY A FACTORY AND NOT A BUILD FLAG
 * ----------------------------------
 * One bundle serves browser and desktop. The right target is whichever bridge is
 * actually present at runtime, decided the same way the print port decides: is
 * there an Electron bridge? If yes, desktop. If not, is there a
 * `showDirectoryPicker`? If so, browser. If neither, the in-memory target, whose
 * `supported` is true but which writes nowhere durable — the UI must therefore
 * check `target.supported` AND treat the in-memory case as "not configured", not
 * as "backing up".
 *
 * This is the ONLY place that knows about the three implementations; the engine
 * and the restore path depend only on the `DiskBackupTarget` interface, so a test
 * injects `InMemoryDiskBackupTarget` and nothing above it changes.
 */

import { InMemoryDiskBackupTarget, type DiskBackupTarget } from './diskBackupTarget';
import { BrowserDiskBackupTarget } from './browserDiskBackupTarget';
import { ElectronDiskBackupTarget, electronBackupBridge } from './electronDiskBackupTarget';

export type DiskBackupPlatform = 'electron' | 'browser' | 'memory';

export interface ResolvedTarget {
  readonly target: DiskBackupTarget;
  readonly platform: DiskBackupPlatform;
  /** True when this platform can back up to a real, durable folder. */
  readonly durable: boolean;
}

export function resolveDiskBackupTarget(): ResolvedTarget {
  if (electronBackupBridge()) {
    return { target: new ElectronDiskBackupTarget(), platform: 'electron', durable: true };
  }
  const browser = new BrowserDiskBackupTarget();
  if (browser.supported) {
    return { target: browser, platform: 'browser', durable: true };
  }
  return { target: new InMemoryDiskBackupTarget(), platform: 'memory', durable: false };
}
